

import { Component, OnInit, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { CourierService } from '../../../shared/services/courier.service';
import { GetFuelListResponse, GetFuelListRequest, PostMainFuelRequest, GetActiveVoucherDto, GetCarAndLoaderResponse, GetCarAndLoaderModel } from '../../../shared/models/courier.models';
import { ActiveUserLocalDataService } from '../../../shared/services/active-user-local-data.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  templateUrl: 'fuel-purchase.component.html',
  styleUrls: ['./fuel-purchase.component.scss']
})
export class FuelPurchaseComponent implements OnInit, OnChanges {
  private lastLoaderState: boolean = false;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCarAndLoader']) {
      const isLoader = this.isLoaderSelected;
      if (!isLoader && this.lastLoaderState) {
        this.engineHours = 0;
      }
      this.lastLoaderState = isLoader;
    }
  }

  fuelResponse: GetFuelListResponse | null = null;
  fuelAmount: number = 0;
  fuelReceiptNumber: string = '';
  engineHours: number = 0;
  popupVisibleError: boolean = false;
  errorMessage: string = '';
  loading: boolean = true;
  isPopupVisible = false;
  isMobile: boolean = false;
  voucher: GetActiveVoucherDto | null = null;
  carAndLoader: GetCarAndLoaderModel[] = [];
  selectedCarAndLoader: number | null = null;

  constructor(
    private courierService: CourierService,
    private activeUserLocalDataService: ActiveUserLocalDataService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
  this.checkIfMobile();
  this.voucher = this.activeUserLocalDataService.getActiveCourierVoucher() || this.activeUserLocalDataService.activeServiceVoucher;

  if (this.voucher) {
    this.courierService.getCarAndLoader({ carId: this.voucher.carsId }).subscribe((response: GetCarAndLoaderResponse) => {

      const carText = this.translate.instant('Car');
      const loaderText = this.translate.instant('Loader');

      this.carAndLoader = response.carAndLoader.map(item => {
        let label = '';
        if (item.carType === 0) label = carText;
        else if (item.carType === 1) label = loaderText;
        return {
          ...item,
          carNumber: label ? `${item.carNumber} (${label})` : item.carNumber
        };
      });

      const car = this.carAndLoader.find(x => x.carType === 0);
      this.selectedCarAndLoader = car ? car.id : (this.carAndLoader[0]?.id ?? null);

      this.checkLoadingComplete();
    }, error => {
      console.error('Error fetching Car and Loader data:', error);
      this.checkLoadingComplete();
    });
  } else {
    this.checkLoadingComplete();
  }

  this.loadFuelList();
}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkIfMobile();
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }

  loadFuelList() {
    this.loading = true;
    let activeVoucher = this.voucher;
  
    if (!activeVoucher) {
      activeVoucher = this.activeUserLocalDataService.getActiveCourierVoucher() || this.activeUserLocalDataService.activeServiceVoucher;
    }
  
    if (activeVoucher) {
      const request: GetFuelListRequest = { voucherId: activeVoucher.id };
      this.courierService.getFuelList(request).subscribe((response: GetFuelListResponse) => {
        this.fuelResponse = response;
        this.checkLoadingComplete();
      }, error => {
        console.error('Error fetching fuel list:', error);
        this.checkLoadingComplete();
      });
    } else {
      console.error('Active voucher is null');
      this.navigateToVoucher();
      this.checkLoadingComplete();
    }
  }

  private checkLoadingComplete() {
    if (this.carAndLoader.length > 0 && this.fuelResponse) {
      this.loading = false;
    }
  }

  navigateToVoucher() {
    const serviceVoucher = this.activeUserLocalDataService.activeServiceVoucher;
    const courierVoucher = this.activeUserLocalDataService.getActiveCourierVoucher();

    if (serviceVoucher) {
      this.router.navigate(['/service-voucher']);
    } else if (courierVoucher) {
      this.router.navigate(['/voucher']);
    } else {
      this.router.navigate(['/voucher']);
    }
  }

  saveClick() {
    const errors: string[] = [];
  
    if (!this.fuelAmount || this.fuelAmount <= 0) {
      errors.push(this.translate.instant('InvalidFuelAmountPleaseEnterAValidFuelAmount'));
    }
    if (this.isLoaderSelected) {
      if (!this.engineHours || this.engineHours < 0) {
        errors.push(this.translate.instant('InvalidEngineHoursPleaseEnterValidEngineHours'));
      }
    }
  
    if (!this.fuelReceiptNumber || this.fuelReceiptNumber.startsWith('-')) {
      errors.push(this.translate.instant('InvalidReceiptNumberPleaseEnterAValidReceiptNumber'));
    }
  
    if (errors.length > 0) {
      this.errorMessage = errors.join('<br/><br/>');
      this.popupVisibleError = true;
      return;
    }
  
    const activeVoucher = this.activeUserLocalDataService.getActiveCourierVoucher() || this.activeUserLocalDataService.activeServiceVoucher;
    if (!activeVoucher) {
      this.navigateToVoucher();
      return;
    }
  
    const request: PostMainFuelRequest = {
      receptNumber: this.fuelReceiptNumber,
      vouchersId: activeVoucher.id,
      fuelAmount: this.fuelAmount
    , engineHours: this.engineHours
    };
  
    if (this.carAndLoader && this.carAndLoader.length > 1 && this.selectedCarAndLoader !== null) {
      const selectedCar = this.carAndLoader.find(car => car.id === this.selectedCarAndLoader);
      if (selectedCar && selectedCar.id !== activeVoucher.carsId) {
        request.loaderId = selectedCar.id;
      }
    }

    this.courierService.postMainFuel(request).subscribe(() => {
      this.navigateToVoucher();
    });
  }
  // Allow both comma and dot as decimal separator for fuelAmount
  onFuelAmountChanged(e: { value: any }) {
    let value = e.value;
    if (typeof value === 'string') {
      value = value.replace(',', '.');
      const parsed = parseFloat(value);
      this.fuelAmount = isNaN(parsed) ? 0 : parsed;
    } else {
      this.fuelAmount = value;
    }
  }
  get isLoaderSelected(): boolean {
    const selected = this.carAndLoader?.find((x: GetCarAndLoaderModel) => x.id === this.selectedCarAndLoader);
    return !!selected && selected.carType === 1;
  }
  // Watch for changes to selectedCarAndLoader
  ngDoCheck(): void {
    const isLoader = this.isLoaderSelected;
    if (isLoader !== this.lastLoaderState) {
      this.engineHours = 0;
      this.fuelAmount = 0;
      this.fuelReceiptNumber = '';
    }
    this.lastLoaderState = isLoader;
  }
}